import React, { useEffect, useRef, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import { login, logout, updateToken } from '../../../app/auth/authSlice';
import { isTokenExpired, shouldRefreshToken, getTimeUntilExpiry } from '../../../utils/tokenUtils';
import { navigateToLogin } from '../../../utils/authRedirect';
import { refreshAccessToken } from '../../../utils/tokenRefresh';
import { getCurrentUser } from '../../../utils/userStorage';

const REFRESH_THRESHOLD_SECONDS = 5 * 60; // refresh 5 minutes before expiry
const ACTIVITY_THROTTLE_MS = 60 * 1000; // at most one activity-driven check per minute
const PERIODIC_CHECK_MS = 60 * 1000; // check every minute while app is open

const TokenExpiryChecker = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isAuthenticated, keepMeLogin } = useSelector((state) => state.auth);
  const intervalRef = useRef(null);
  const refreshTimeoutRef = useRef(null);
  const hasRedirectedRef = useRef(false);
  const isRefreshingRef = useRef(false);
  const lastActivityCheckRef = useRef(0);

  const isOnLoginPage = location.pathname.includes('/user/login');

  useEffect(() => {
    if (!isOnLoginPage) {
      hasRedirectedRef.current = false;
    }
  }, [location.pathname, isOnLoginPage]);

  const safeRedirectToLogin = useCallback(() => {
    if (!hasRedirectedRef.current && !isOnLoginPage) {
      hasRedirectedRef.current = true;
      dispatch(logout());
      navigateToLogin(navigate, location);
    }
  }, [dispatch, navigate, location, isOnLoginPage]);

  const renewToken = useCallback(async () => {
    if (isRefreshingRef.current) return false;
    isRefreshingRef.current = true;
    try {
      await refreshAccessToken();
      return true;
    } catch (error) {
      console.warn('Session token renewal failed:', error?.message || error);
      return false;
    } finally {
      isRefreshingRef.current = false;
    }
  }, []);

  const ensureValidSession = useCallback(async () => {
    const currentUser = getCurrentUser() || user;
    const token = currentUser?.token;
    if (!token) {
      safeRedirectToLogin();
      return;
    }

    if (isTokenExpired(token) || shouldRefreshToken(token, REFRESH_THRESHOLD_SECONDS)) {
      const renewed = await renewToken();
      if (!renewed && isTokenExpired(token)) {
        safeRedirectToLogin();
      }
    }
  }, [user, renewToken, safeRedirectToLogin]);

  const scheduleProactiveRefresh = useCallback((token) => {
    if (refreshTimeoutRef.current) {
      clearTimeout(refreshTimeoutRef.current);
      refreshTimeoutRef.current = null;
    }

    if (!token || isTokenExpired(token)) return;

    const timeUntilExpiry = getTimeUntilExpiry(token);
    if (timeUntilExpiry <= 0) return;

    const minDelay = 30 * 1000;
    let refreshDelay;
    if (timeUntilExpiry <= REFRESH_THRESHOLD_SECONDS) {
      refreshDelay = minDelay;
    } else {
      refreshDelay = Math.max((timeUntilExpiry - REFRESH_THRESHOLD_SECONDS) * 1000, minDelay);
    }

    refreshTimeoutRef.current = setTimeout(async () => {
      const renewed = await renewToken();
      if (!renewed) {
        const latest = getCurrentUser();
        if (!latest?.token || isTokenExpired(latest.token)) {
          safeRedirectToLogin();
        }
      }
      // Successful renew updates Redux user → effect re-schedules next refresh
    }, refreshDelay);
  }, [renewToken, safeRedirectToLogin]);

  // Keep session alive while the user is actively using BackOffice
  useEffect(() => {
    if (isOnLoginPage || !isAuthenticated || !user?.token) return;

    ensureValidSession();
    scheduleProactiveRefresh(user.token);

    intervalRef.current = setInterval(() => {
      ensureValidSession();
    }, PERIODIC_CHECK_MS);

    const onActivity = () => {
      const now = Date.now();
      if (now - lastActivityCheckRef.current < ACTIVITY_THROTTLE_MS) return;
      lastActivityCheckRef.current = now;

      const token = getCurrentUser()?.token || user.token;
      if (token && shouldRefreshToken(token, REFRESH_THRESHOLD_SECONDS)) {
        renewToken();
      }
    };

    const activityEvents = ['mousedown', 'keydown', 'touchstart', 'scroll', 'click'];
    activityEvents.forEach((evt) =>
      window.addEventListener(evt, onActivity, { passive: true })
    );

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      if (refreshTimeoutRef.current) clearTimeout(refreshTimeoutRef.current);
      activityEvents.forEach((evt) => window.removeEventListener(evt, onActivity));
    };
  }, [
    user?.token,
    isAuthenticated,
    isOnLoginPage,
    ensureValidSession,
    scheduleProactiveRefresh,
    renewToken,
  ]);

  // Sync login from other tabs; logout if session cleared elsewhere
  useEffect(() => {
    if (isOnLoginPage) return;

    const handleStorageChange = (e) => {
      if (!e.key || (!e.key.startsWith('user_') && e.key !== 'activeUser')) return;

      if (e.key === 'activeUser' && !e.newValue) {
        safeRedirectToLogin();
        return;
      }

      if (e.key.startsWith('user_') && !e.newValue && e.key === sessionStorage.getItem('activeUser')) {
        safeRedirectToLogin();
        return;
      }

      if (e.key.startsWith('user_') && e.newValue) {
        try {
          const newUser = JSON.parse(e.newValue);
          if (newUser?.token && !isTokenExpired(newUser.token)) {
            dispatch(login(newUser));
          }
        } catch {
          // ignore invalid storage payloads
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [dispatch, isOnLoginPage, safeRedirectToLogin]);

  // When tab becomes visible, renew if needed instead of logging out
  useEffect(() => {
    if (isOnLoginPage || !isAuthenticated || !user) return;

    const handleVisibilityChange = () => {
      if (!document.hidden) {
        ensureValidSession();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [user, isAuthenticated, isOnLoginPage, ensureValidSession]);

  // Global events from axios / tokenRefresh
  useEffect(() => {
    const handleTokenRefresh = (event) => {
      const { token, refreshToken, keepMeLogin: refreshedKeepMeLogin, subscriptionAccess } =
        event.detail || {};
      dispatch(
        updateToken({
          token,
          refreshToken,
          keepMeLogin: refreshedKeepMeLogin ?? keepMeLogin,
          subscriptionAccess,
        })
      );
    };

    const handleForceLogout = () => {
      safeRedirectToLogin();
    };

    window.addEventListener('tokenRefreshed', handleTokenRefresh);
    window.addEventListener('forceLogout', handleForceLogout);

    return () => {
      window.removeEventListener('tokenRefreshed', handleTokenRefresh);
      window.removeEventListener('forceLogout', handleForceLogout);
    };
  }, [dispatch, keepMeLogin, safeRedirectToLogin]);

  return null;
};

export default TokenExpiryChecker;
